import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

import { Button } from "./button";

const MIME_BY_EXTENSION: Record<string, string> = {
  gif: "image/gif",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
};

type PreviewFile = {
  id: string;
  file: File;
  previewUrl: string;
};

function normalizeExtensions(allowedExtensions: string[]) {
  return allowedExtensions.map((extension) =>
    extension.toLowerCase().replace(/^\./, ""),
  );
}

function buildAccept(allowedExtensions: string[]) {
  const extensions = normalizeExtensions(allowedExtensions);
  const mimeTypes = [
    ...new Set(
      extensions
        .map((extension) => MIME_BY_EXTENSION[extension])
        .filter(Boolean),
    ),
  ];
  return [...extensions.map((extension) => `.${extension}`), ...mimeTypes].join(
    ",",
  );
}

function isAllowedFile(file: File, allowedExtensions: string[]) {
  const extensions = normalizeExtensions(allowedExtensions);
  const fileExtension = file.name.split(".").pop()?.toLowerCase();
  if (fileExtension && extensions.includes(fileExtension)) {
    return true;
  }

  const extensionForMime = Object.entries(MIME_BY_EXTENSION).find(
    ([, mimeType]) => mimeType === file.type.toLowerCase(),
  )?.[0];
  return (
    extensionForMime !== undefined && extensions.includes(extensionForMime)
  );
}

function createPreviewFile(file: File): PreviewFile {
  return {
    id: crypto.randomUUID(),
    file,
    previewUrl: URL.createObjectURL(file),
  };
}

export function FileUpload({
  allowedExtensions,
  children,
  disabled = false,
  onChange,
}: {
  allowedExtensions: string[];
  children?: React.ReactNode;
  disabled?: boolean;
  onChange: (files: File[]) => void;
}) {
  const inputId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);

  const [previewFiles, setPreviewFiles] = useState<PreviewFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    return () => {
      for (const previewFile of previewFiles) {
        URL.revokeObjectURL(previewFile.previewUrl);
      }
    };
  }, [previewFiles]);

  const accept = useMemo(
    () => buildAccept(allowedExtensions),
    [allowedExtensions],
  );

  const updateFiles = useCallback(
    (updater: (current: PreviewFile[]) => PreviewFile[]) => {
      setPreviewFiles((current) => {
        const next = updater(current);
        onChange(next.map((previewFile) => previewFile.file));
        return next;
      });
    },
    [onChange],
  );

  const addFiles = useCallback(
    (files: FileList | File[] | null | undefined) => {
      if (!files || disabled) {
        return;
      }

      const allowedFiles = Array.from(files).filter((file) =>
        isAllowedFile(file, allowedExtensions),
      );
      if (allowedFiles.length === 0) {
        return;
      }

      updateFiles((current) => [
        ...current,
        ...allowedFiles.map((file) => createPreviewFile(file)),
      ]);
    },
    [allowedExtensions, disabled, updateFiles],
  );

  const removeFile = useCallback(
    (id: string) => {
      if (disabled) {
        return;
      }
      updateFiles((current) => {
        const removed = current.find((previewFile) => previewFile.id === id);
        if (removed) {
          URL.revokeObjectURL(removed.previewUrl);
        }
        return current.filter((previewFile) => previewFile.id !== id);
      });
    },
    [disabled, updateFiles],
  );

  const openFilePicker = useCallback(() => {
    if (disabled) {
      return;
    }
    fileInputRef.current?.click();
  }, [disabled]);

  const getFilesFromClipboard = useCallback(
    (clipboardData: DataTransfer | null) => {
      if (!clipboardData) {
        return [];
      }

      const files: File[] = [];
      const items = clipboardData.items;

      if (items) {
        for (let index = 0; index < items.length; index += 1) {
          const item = items[index];
          if (!item?.type.startsWith("image/")) {
            continue;
          }
          const file = item.getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }

      if (files.length === 0 && clipboardData.files.length > 0) {
        for (let index = 0; index < clipboardData.files.length; index += 1) {
          const file = clipboardData.files[index];
          if (file?.type.startsWith("image/")) {
            files.push(file);
          }
        }
      }

      return files;
    },
    [],
  );

  return (
    <div className="flex w-full flex-col gap-2">
      <input
        ref={fileInputRef}
        accept={accept}
        className="sr-only"
        disabled={disabled}
        id={inputId}
        multiple={true}
        type="file"
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = "";
        }}
      />
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {previewFiles.map((previewFile) => (
          <div
            key={previewFile.id}
            className="relative min-w-0 rounded-lg border border-gray-200 bg-white p-2"
          >
            <button
              aria-label={`Remove ${previewFile.file.name}`}
              className="absolute top-1 right-1 z-10 flex h-7 w-7 cursor-pointer items-center justify-center rounded-md bg-gray-500 text-white disabled:cursor-not-allowed disabled:opacity-50"
              disabled={disabled}
              type="button"
              onClick={() => {
                removeFile(previewFile.id);
              }}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
            <div className="flex h-[100px] items-center justify-center">
              <img
                alt={previewFile.file.name}
                className="max-h-full max-w-full rounded object-contain"
                src={previewFile.previewUrl}
              />
            </div>
            <p className="mt-1 w-full truncate text-xs font-medium text-gray-900">
              {previewFile.file.name}
            </p>
          </div>
        ))}
        <div
          aria-label="Drop images or paste from clipboard"
          className={`relative min-w-0 rounded-lg border-2 border-dashed p-2 text-center transition-colors outline-none ${
            disabled
              ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400"
              : isDragOver
                ? "border-blue-500 bg-blue-50 text-blue-700"
                : isFocused
                  ? "border-blue-400 bg-blue-50/50 text-gray-700"
                  : "border-gray-300 bg-white text-gray-600"
          }`.trim()}
          role="region"
          tabIndex={disabled ? -1 : 0}
          onBlur={() => {
            setIsFocused(false);
          }}
          onDragEnter={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (disabled) {
              return;
            }
            dragCounterRef.current += 1;
            setIsDragOver(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.stopPropagation();
            dragCounterRef.current -= 1;
            if (dragCounterRef.current <= 0) {
              dragCounterRef.current = 0;
              setIsDragOver(false);
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            dragCounterRef.current = 0;
            setIsDragOver(false);
            addFiles(e.dataTransfer.files);
          }}
          onFocus={() => {
            setIsFocused(true);
          }}
          onPaste={(e) => {
            if (disabled) {
              return;
            }

            const pastedFiles = getFilesFromClipboard(e.clipboardData);
            if (pastedFiles.length > 0) {
              e.preventDefault();
              addFiles(pastedFiles);
            }
          }}
        >
          <div className="flex h-[100px] flex-col items-center justify-center px-1">
            <p className="text-xs font-medium">
              {previewFiles.length > 0 ? "Add more" : "Drop images"}
            </p>
            <p className="mt-1 text-[10px] leading-tight text-gray-500">
              Drop or focus and paste
            </p>
          </div>
          <p className="mt-1 w-full truncate text-xs font-medium text-gray-500">
            Add images
          </p>
        </div>
      </div>
      <div className="flex gap-2">
        <Button
          className="flex-1"
          disabled={disabled}
          type="button"
          onClick={openFilePicker}
        >
          Select images
        </Button>
        {children}
      </div>
    </div>
  );
}
